/**
 * ===========================================================================
 *  sfx.js — the synthesised sound-effect library
 * ===========================================================================
 * Every entry is a recipe, not a recording. A build function receives
 *
 *    A = { ctx, g:graph, out:<node to write into>, rnd, n:{white,pink,brown} }
 *    t = AudioContext time to start at
 *    p = { gain (0..2, doubles as "how hard was it"), rate (pitch scale) }
 *
 * and wires oscillators / noise slices / filters into `A.out`. Every recipe
 * jitters its own frequencies and noise offsets from `rnd()` (a fork of
 * world.rng), so the fortieth sword hit in a musou is a different sword hit.
 *
 * `id`s marked ← are the ones other modules already call by name
 * (src/combat/moveset.js, src/combat/combat.js, src/game/player.js,
 * src/anim/clips.js); those names are frozen.
 * ===========================================================================
 */
import {
  env, envASR, sweep, gainNode, filter, noiseShot, metalBank, bodyThump,
  noiseBand, transient, FLOOR,
} from './synth.js';

const R = (rnd, a, b) => a + (b - a) * rnd();
const J = (rnd, v, amt) => v * (1 + (rnd() * 2 - 1) * amt);

/* --------------------------------------------------------------- swings -- */
/* A whoosh is band-limited noise whose centre frequency arcs up and back down
 * as the blade passes: that arc IS the Doppler of the swing. Heavier weapons
 * get more low content and a longer arc. */
function whoosh(A, t, o) {
  const rnd = A.rnd;
  const dur = J(rnd, o.dur, 0.10);
  noiseBand(A.ctx, A.out, t, A.n.white, {
    dur, f0: J(rnd, o.f0, 0.10), fMid: J(rnd, o.fMid, 0.12), f1: J(rnd, o.f1, 0.10),
    q: o.q, gain: o.gain, atk: dur * 0.24, dec: dur * 0.8,
  }, rnd);
  // air/body layer — the low "wumph" that tells you the thing has mass
  noiseBand(A.ctx, A.out, t + 0.004, A.n.pink, {
    dur: dur * 1.15, f0: J(rnd, o.lo0, 0.12), fMid: J(rnd, o.lo1, 0.12), f1: J(rnd, o.lo0 * 0.7, 0.1),
    q: 0.8, gain: o.loGain, atk: dur * 0.3, dec: dur * 0.9,
  }, rnd);
  if (o.edge) {
    // a faint ringing edge for the big two-handers
    metalBank(A.ctx, A.out, t + dur * 0.35, {
      f0: J(rnd, 1750, 0.12), dur: dur * 1.4, gain: o.edge, n: 3,
      bp: 2600, bpTo: 1500, q: 1.1, atk: 0.02,
    }, rnd);
  }
}

/* -------------------------------------------------------------- impacts -- */

function metalImpact(A, t, o) {
  const rnd = A.rnd, p = o.power;
  transient(A.ctx, A.out, t, A.n.white, { dur: 0.005, f0: 3000, gain: 0.55 * p }, rnd);
  // the cut: bright noise collapsing downward in 80 ms
  noiseBand(A.ctx, A.out, t, A.n.white, {
    dur: o.cut ?? 0.085, f0: J(rnd, 3000, 0.15), f1: J(rnd, 850, 0.15),
    q: 1.2, gain: 0.44 * p, atk: 0.001, dec: (o.cut ?? 0.085) * 0.9,
  }, rnd);
  // the metallic tail — inharmonic partials, this is what says "steel"
  metalBank(A.ctx, A.out, t + 0.002, {
    f0: J(rnd, o.ring ?? 1650, 0.14), dur: o.tail ?? 0.62, gain: (o.ringGain ?? 0.44) * p,
    bp: 3100, bpTo: 1300, q: 0.95, jitter: 0.08,
  }, rnd);
  // meat + bone under the steel
  bodyThump(A.ctx, A.out, t + 0.001, {
    f0: J(rnd, o.body ?? 200, 0.12), f1: J(rnd, 52, 0.15),
    dur: o.bodyDur ?? 0.17, gain: (o.bodyGain ?? 0.55) * p,
  }, rnd);
  noiseBand(A.ctx, A.out, t + 0.002, A.n.brown, {
    dur: 0.07, type: 'lowpass', f0: 620, f1: 220, q: 0.7, gain: 0.30 * p, atk: 0.001, dec: 0.06,
  }, rnd);
}

function bluntImpact(A, t, o) {
  const rnd = A.rnd, p = o.power;
  transient(A.ctx, A.out, t, A.n.white, { dur: 0.004, type: 'lowpass', f0: 1800, gain: 0.5 * p }, rnd);
  bodyThump(A.ctx, A.out, t, {
    f0: J(rnd, o.f0 ?? 168, 0.10), f1: J(rnd, o.f1 ?? 42, 0.12),
    dur: o.dur ?? 0.27, gain: 0.85 * p, pitchDur: 0.45,
  }, rnd);
  noiseBand(A.ctx, A.out, t, A.n.brown, {
    dur: (o.dur ?? 0.27) * 0.5, type: 'lowpass', f0: J(rnd, 780, 0.15), f1: 170,
    q: 0.8, gain: 0.36 * p, atk: 0.0015, dec: (o.dur ?? 0.27) * 0.5,
  }, rnd);
  // a hard woody knock so it does not read as pure sub
  noiseBand(A.ctx, A.out, t, A.n.white, {
    dur: 0.055, f0: J(rnd, 820, 0.18), f1: J(rnd, 520, 0.15), q: 6, gain: 0.52 * p, atk: 0.001, dec: 0.05,
  }, rnd);
}

/* ------------------------------------------------------------ the table -- */

export const SFX = {

  /* --- swings ------------------------------------------------------------ */
  swing_fast: { bus: 'sfx', gap: 0.035, len: 0.22, build: (A, t, p) =>       /* ← moveset */
    whoosh(A, t, { dur: 0.135, f0: 620, fMid: 2900, f1: 950, q: 2.1, gain: 0.55 * p.gain, lo0: 240, lo1: 620, loGain: 0.20 * p.gain }) },
  swing_light: { bus: 'sfx', gap: 0.035, len: 0.30, build: (A, t, p) =>      /* ← moveset */
    whoosh(A, t, { dur: 0.19, f0: 430, fMid: 2150, f1: 700, q: 1.7, gain: 0.62 * p.gain, lo0: 185, lo1: 480, loGain: 0.30 * p.gain }) },
  swing_heavy: { bus: 'sfx', gap: 0.04, len: 0.46, build: (A, t, p) =>       /* ← moveset */
    whoosh(A, t, { dur: 0.30, f0: 265, fMid: 1550, f1: 420, q: 1.2, gain: 0.66 * p.gain, lo0: 130, lo1: 380, loGain: 0.46 * p.gain, edge: 0.09 * p.gain }) },
  swing_huge: { bus: 'sfx', gap: 0.05, len: 0.70, build: (A, t, p) => {      /* ← moveset */
    whoosh(A, t, { dur: 0.44, f0: 200, fMid: 1150, f1: 300, q: 1.0, gain: 0.70 * p.gain, lo0: 95, lo1: 300, loGain: 0.58 * p.gain, edge: 0.14 * p.gain });
    bodyThump(A.ctx, A.out, t + 0.18, { f0: 150, f1: 46, dur: 0.30, gain: 0.22 * p.gain }, A.rnd);
  } },
  swing: { alias: 'swing_light' },                                            /* ← moveset default */

  /* --- blade / body impacts --------------------------------------------- */
  slash: { bus: 'sfx', gap: 0.022, len: 0.50, build: (A, t, p) => metalImpact(A, t, { power: p.gain }) },
  impact_slash: { alias: 'slash' },
  hit: { alias: 'slash' },
  /* `ring` is the metal bank's fundamental, and 2050 put it — and every
   * inharmonic partial above it — past the 2 kHz band edge. Measured offline
   * through the real graph, hit_light came out 0.11 low / 0.00 mid / 0.89 high:
   * the only id in the library with a literally empty 250-2000 Hz midrange,
   * against slash 0.47, guard 0.44 and hit_heavy 0.42. It is also the most
   * triggered sound in the game by a wide margin (28-46 per probe segment), so
   * a musou that lands forty of them stacked forty copies of a sound with no
   * body. Dropped to 1520 to seat the fundamental inside the band, with `body`
   * named explicitly at 210 rather than left to metalImpact's 200 default so
   * the thump under the steel is a decision and not an accident. */
  hit_light: { bus: 'sfx', gap: 0.022, len: 0.40, build: (A, t, p) =>
    metalImpact(A, t, { power: p.gain * 0.8, ring: 1520, tail: 0.42, ringGain: 0.38, cut: 0.06, body: 210, bodyDur: 0.14, bodyGain: 0.46 }) },
  hit_heavy: { bus: 'sfx', gap: 0.03, len: 0.72, build: (A, t, p) =>
    metalImpact(A, t, { power: p.gain, ring: 1250, tail: 0.80, ringGain: 0.48, cut: 0.11, body: 165, bodyDur: 0.26, bodyGain: 0.72 }) },
  hit_crit: { bus: 'sfx', gap: 0.03, len: 0.9, build: (A, t, p) => {
    metalImpact(A, t, { power: p.gain, ring: 1450, tail: 0.95, ringGain: 0.52, cut: 0.13, body: 150, bodyDur: 0.30, bodyGain: 0.8 });
    metalBank(A.ctx, A.out, t + 0.02, { f0: J(A.rnd, 3300, 0.1), dur: 0.5, gain: 0.14 * p.gain, n: 4, bp: 5200, bpTo: 2600, q: 1.4 }, A.rnd);
  } },

  impact_blunt: { bus: 'sfx', gap: 0.03, len: 0.50, build: (A, t, p) =>      /* ← moveset (stun) */
    bluntImpact(A, t, { power: p.gain }) },

  impact_slam: { bus: 'sfx', gap: 0.05, len: 1.2, build: (A, t, p) => {      /* ← moveset (ground slam) */
    const rnd = A.rnd, g = p.gain;
    bluntImpact(A, t, { power: g * 1.1, f0: 130, f1: 30, dur: 0.5 });
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 70, 0.08), f1: 26, dur: 0.75, gain: 0.42 * g, atk: 0.006, pitchDur: 0.7 }, rnd);
    // debris / dirt thrown out of the crater
    noiseBand(A.ctx, A.out, t + 0.01, A.n.pink, {
      dur: 0.62, f0: J(rnd, 1500, 0.15), f1: 190, q: 0.9, gain: 0.55 * g, atk: 0.004, dec: 0.6,
    }, rnd);
    metalBank(A.ctx, A.out, t, { f0: J(rnd, 900, 0.12), dur: 0.55, gain: 0.26 * g, n: 4, bp: 1900, bpTo: 800, q: 1.1 }, rnd);
  } },

  impact_musou: { bus: 'sfx', gap: 0.05, len: 1.8, build: (A, t, p) => {     /* ← moveset (musou) */
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.008, f0: 2400, gain: 0.7 * g }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 190, 0.08), f1: 34, dur: 0.9, gain: 0.95 * g, pitchDur: 0.35 }, rnd);
    bodyThump(A.ctx, A.out, t + 0.004, { f0: 62, f1: 24, dur: 1.25, gain: 0.6 * g, atk: 0.01, pitchDur: 0.8 }, rnd);
    // the shear: a bright band ripping upward out of the hit
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.22, f0: 500, f1: 7200, q: 1.1, gain: 0.34 * g, atk: 0.002, dec: 0.2 }, rnd);
    metalBank(A.ctx, A.out, t + 0.002, { f0: J(rnd, 780, 0.10), dur: 1.15, gain: 0.34 * g, bp: 2400, bpTo: 900, q: 0.8, jitter: 0.1 }, rnd);
    metalBank(A.ctx, A.out, t + 0.05, { f0: J(rnd, 2600, 0.10), dur: 0.85, gain: 0.16 * g, n: 4, bp: 4600, bpTo: 2200, q: 1.3 }, rnd);
  } },

  /* --- guard ------------------------------------------------------------- */
  guard: { bus: 'sfx', gap: 0.03, len: 1.0, build: (A, t, p) => {            /* ← combat.js */
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.004, f0: 4200, gain: 0.5 * g }, rnd);
    metalBank(A.ctx, A.out, t, {
      f0: J(rnd, 1580, 0.10), dur: 0.85, gain: 0.50 * g, bp: 3000, bpTo: 1500, q: 1.4,
      ratios: [1, 1.36, 1.94, 2.51, 3.33, 4.61], jitter: 0.07,
    }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.07, f0: 760, f1: 420, q: 3.2, gain: 0.34 * g, atk: 0.001, dec: 0.065 }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: 165, f1: 58, dur: 0.17, gain: 0.60 * g }, rnd);
  } },
  clang: { alias: 'guard' },

  guardBreak: { bus: 'sfx', gap: 0.06, len: 1.2, build: (A, t, p) => {       /* ← clips.js */
    const rnd = A.rnd, g = p.gain;
    metalBank(A.ctx, A.out, t, {
      f0: J(rnd, 2400, 0.08), dur: 0.85, gain: 0.45 * g, bp: 4200, bpTo: 900, q: 1.2, bend: 0.55, jitter: 0.1,
    }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.38, f0: 5200, f1: 600, q: 1.0, gain: 0.34 * g, atk: 0.002, dec: 0.36 }, rnd);
    bodyThump(A.ctx, A.out, t + 0.01, { f0: 130, f1: 34, dur: 0.45, gain: 0.55 * g }, rnd);
  } },
  guard_break: { alias: 'guardBreak' },

  /* --- bodies ------------------------------------------------------------ */
  bodyfall: { bus: 'sfx', gap: 0.03, len: 0.8, build: (A, t, p) => {         /* ← combat.js, clips.js */
    const rnd = A.rnd, g = p.gain;
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 104, 0.12), f1: 36, dur: 0.32, gain: 0.62 * g }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.brown, { dur: 0.26, type: 'lowpass', f0: J(rnd, 900, 0.2), f1: 190, q: 0.7, gain: 0.34 * g, atk: 0.002, dec: 0.25 }, rnd);
    // armour clatter: three or four small plates settling
    const k = 3 + Math.floor(rnd() * 3);
    for (let i = 0; i < k; i++) {
      metalBank(A.ctx, A.out, t + 0.02 + rnd() * 0.19, {
        f0: R(rnd, 2200, 4200), dur: R(rnd, 0.04, 0.11), gain: 0.055 * g, n: 2, bp: 4000, q: 2.0,
      }, rnd);
    }
  } },

  roll: { bus: 'sfx', gap: 0.05, len: 0.7, build: (A, t, p) => {             /* ← player.js, clips.js */
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t, A.n.pink, {
      dur: 0.40, f0: J(rnd, 320, 0.12), fMid: 1500, f1: 380, q: 1.1, gain: 0.30 * g, atk: 0.05, dec: 0.34,
    }, rnd);
    noiseBand(A.ctx, A.out, t + 0.03, A.n.brown, { dur: 0.30, type: 'lowpass', f0: 500, f1: 200, q: 0.7, gain: 0.22 * g, atk: 0.03, dec: 0.28 }, rnd);
    for (let i = 0; i < 4; i++) {
      metalBank(A.ctx, A.out, t + 0.01 + i * 0.09 + rnd() * 0.04, { f0: R(rnd, 2600, 5000), dur: R(rnd, 0.03, 0.08), gain: 0.05 * g, n: 2, bp: 4600, q: 2.4 }, rnd);
    }
  } },

  armour: { bus: 'sfx', gap: 0.10, len: 0.4, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    const k = 4 + Math.floor(rnd() * 3);
    for (let i = 0; i < k; i++) {
      const tt = t + rnd() * 0.16;
      noiseBand(A.ctx, A.out, tt, A.n.white, { dur: R(rnd, 0.012, 0.03), f0: R(rnd, 2400, 6200), q: 3.5, gain: 0.16 * g, atk: 0.001, dec: 0.02 }, rnd);
      if (i < 2) metalBank(A.ctx, A.out, tt, { f0: R(rnd, 3000, 5200), dur: 0.07, gain: 0.05 * g, n: 2, bp: 5000, q: 2.6 }, rnd);
    }
  } },

  launcher: { bus: 'sfx', gap: 0.05, len: 0.8, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.005, f0: 2600, gain: 0.4 * g }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.36, f0: J(rnd, 300, 0.1), f1: J(rnd, 3800, 0.1), q: 1.5, gain: 0.30 * g, atk: 0.01, dec: 0.34 }, rnd);
    const o1 = A.ctx.createOscillator(); o1.type = 'triangle';
    sweep(o1.frequency, t, J(rnd, 180, 0.08), J(rnd, 980, 0.08), 0.34);
    const gg = gainNode(A.ctx, 0); env(gg.gain, t, 0.22 * g, 0.012, 0.34);
    o1.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(t + 0.42);
    bodyThump(A.ctx, A.out, t, { f0: 175, f1: 55, dur: 0.2, gain: 0.5 * g }, rnd);
  } },

  /* --- musou ------------------------------------------------------------- */
  musou_start: { bus: 'sfx', gap: 0.2, len: 2.2, build: (A, t, p) => {       /* ← player.js */
    const rnd = A.rnd, g = p.gain;
    // a rising swell that lands on a sub drop — the "the screen is about to go white" cue
    noiseBand(A.ctx, A.out, t, A.n.pink, { dur: 0.70, f0: 180, f1: 5600, q: 1.0, gain: 0.30 * g, atk: 0.62, dec: 0.28 }, rnd);
    const sw = A.ctx.createOscillator(); sw.type = 'sawtooth';
    sweep(sw.frequency, t, 70, 320, 0.72);
    const swf = filter(A.ctx, 'lowpass', 300, 6); sweep(swf.frequency, t, 300, 3800, 0.72);
    const swg = gainNode(A.ctx, 0); envASR(swg.gain, t, 0.24 * g, 0.6, 0.06, 0.4);
    sw.connect(swf); swf.connect(swg); swg.connect(A.out); sw.start(t); sw.stop(t + 1.3);
    // the drop
    bodyThump(A.ctx, A.out, t + 0.70, { f0: 210, f1: 28, dur: 1.05, gain: 0.9 * g, pitchDur: 0.4 }, rnd);
    metalBank(A.ctx, A.out, t + 0.70, { f0: J(rnd, 740, 0.08), dur: 1.4, gain: 0.28 * g, bp: 2200, bpTo: 700, q: 0.8 }, rnd);
    transient(A.ctx, A.out, t + 0.70, A.n.white, { dur: 0.01, f0: 2000, gain: 0.6 * g }, rnd);
  } },
  musou_charge: { bus: 'sfx', gap: 0.3, len: 1.6, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t, A.n.pink, { dur: 1.2, f0: 220, f1: 4200, q: 1.2, gain: 0.22 * g, atk: 1.0, dec: 0.35 }, rnd);
    const o1 = A.ctx.createOscillator(); o1.type = 'sawtooth';
    sweep(o1.frequency, t, 110, 440, 1.2);
    const f1 = filter(A.ctx, 'bandpass', 400, 4); sweep(f1.frequency, t, 400, 2600, 1.2);
    const gg = gainNode(A.ctx, 0); envASR(gg.gain, t, 0.18 * g, 1.0, 0.05, 0.3);
    o1.connect(f1); f1.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(t + 1.5);
  } },
  musou_release: { alias: 'impact_musou' },

  /* --- footsteps --------------------------------------------------------- */
  footstep_grass: { bus: 'sfx', gap: 0.045, len: 0.22, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.004, f0: 3200, gain: 0.22 * g }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.062, f0: J(rnd, 2500, 0.2), f1: J(rnd, 1400, 0.2), q: 1.3, gain: 0.50 * g, atk: 0.002, dec: 0.06 }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 96, 0.15), f1: 52, dur: 0.07, gain: 0.11 * g }, rnd);
  } },
  footstep_dirt: { bus: 'sfx', gap: 0.045, len: 0.24, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.075, f0: J(rnd, 1500, 0.2), f1: J(rnd, 520, 0.2), q: 0.9, gain: 0.62 * g, atk: 0.0015, dec: 0.07 }, rnd);
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.004, f0: 2400, gain: 0.20 * g }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 78, 0.15), f1: 40, dur: 0.09, gain: 0.14 * g }, rnd);
  } },
  footstep_stone: { bus: 'sfx', gap: 0.045, len: 0.30, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.0035, f0: 4600, gain: 0.34 * g }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.045, f0: J(rnd, 3300, 0.18), f1: J(rnd, 2100, 0.18), q: 2.2, gain: 0.46 * g, atk: 0.001, dec: 0.042 }, rnd);
    metalBank(A.ctx, A.out, t, { f0: J(rnd, 2300, 0.12), dur: 0.13, gain: 0.13 * g, n: 3, bp: 3400, q: 2.2 }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 118, 0.12), f1: 62, dur: 0.06, gain: 0.12 * g }, rnd);
  } },
  footstep: { alias: 'footstep_dirt' },
  footstep_sand: { alias: 'footstep_dirt' },

  /* --- archery ----------------------------------------------------------- */
  bow: { bus: 'sfx', gap: 0.04, len: 0.4, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t, A.n.white, { dur: 0.14, f0: J(rnd, 900, 0.12), f1: J(rnd, 420, 0.12), q: 7, gain: 0.62 * g, atk: 0.001, dec: 0.13 }, rnd);
    const o1 = A.ctx.createOscillator(); o1.type = 'triangle';
    sweep(o1.frequency, t, J(rnd, 340, 0.1), 150, 0.12);
    const gg = gainNode(A.ctx, 0); env(gg.gain, t, 0.24 * g, 0.001, 0.12);
    o1.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(t + 0.2);
    noiseBand(A.ctx, A.out, t + 0.02, A.n.white, { dur: 0.16, f0: 2600, f1: 5200, q: 1.4, gain: 0.045 * g, atk: 0.03, dec: 0.14 }, rnd);
  } },
  arrow_impact: { bus: 'sfx', gap: 0.03, len: 0.5, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t - 0.0, A.n.white, { dur: 0.05, f0: 5200, f1: 1400, q: 1.2, gain: 0.24 * g, atk: 0.001, dec: 0.048 }, rnd);
    transient(A.ctx, A.out, t + 0.02, A.n.white, { dur: 0.005, f0: 2600, gain: 0.5 * g }, rnd);
    metalBank(A.ctx, A.out, t + 0.02, { f0: J(rnd, 1450, 0.14), dur: 0.20, gain: 0.20 * g, n: 3, ratios: [1, 2.31, 3.72], bp: 2400, bpTo: 1200, q: 1.6 }, rnd);
    bodyThump(A.ctx, A.out, t + 0.02, { f0: J(rnd, 150, 0.12), f1: 58, dur: 0.13, gain: 0.45 * g }, rnd);
  } },

  /* --- world ------------------------------------------------------------- */
  gate_open: { bus: 'sfx', gap: 0.5, len: 3.2, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    // a heavy timber gate: a resonant creak that stutters, over a low rumble
    const o1 = A.ctx.createOscillator(); o1.type = 'sawtooth';
    o1.frequency.setValueAtTime(J(rnd, 44, 0.1), t);
    const bp = filter(A.ctx, 'bandpass', 220, 9);
    const gg = gainNode(A.ctx, 0);
    gg.gain.setValueAtTime(FLOOR, t);
    let tt = t + 0.05, f = 200;
    for (let i = 0; i < 9; i++) {                 // stick-slip: the creak is not smooth
      const d = R(rnd, 0.12, 0.30);
      f = R(rnd, 180, 620);
      bp.frequency.linearRampToValueAtTime(f, tt + d * 0.8);
      gg.gain.linearRampToValueAtTime(R(rnd, 0.06, 0.20) * g, tt + d * 0.5);
      gg.gain.linearRampToValueAtTime(R(rnd, 0.01, 0.06) * g, tt + d);
      tt += d;
    }
    gg.gain.linearRampToValueAtTime(FLOOR, tt + 0.3);
    o1.connect(bp); bp.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(tt + 0.5);
    noiseBand(A.ctx, A.out, t, A.n.brown, { dur: 2.3, type: 'lowpass', f0: 150, f1: 70, q: 0.6, gain: 0.26 * g, atk: 0.3, dec: 2.0 }, rnd);
    bodyThump(A.ctx, A.out, tt + 0.05, { f0: 90, f1: 30, dur: 0.7, gain: 0.7 * g }, rnd);   // the stop
  } },

  banner_flap: { bus: 'ambience', gap: 0.2, len: 0.9, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    for (let i = 0; i < 3; i++) {
      const tt = t + i * R(rnd, 0.10, 0.19);
      noiseBand(A.ctx, A.out, tt, A.n.pink, {
        dur: R(rnd, 0.09, 0.15), f0: J(rnd, 700, 0.2), fMid: J(rnd, 2400, 0.2), f1: J(rnd, 900, 0.2),
        q: 1.1, gain: (0.62 - i * 0.14) * g, atk: 0.02, dec: 0.12,
      }, rnd);
    }
  } },

  fire_crackle: { bus: 'ambience', gap: 0.1, len: 1.5, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    noiseBand(A.ctx, A.out, t, A.n.brown, { dur: 1.2, type: 'lowpass', f0: 900, f1: 620, q: 0.7, gain: 0.20 * g, atk: 0.15, hold: 0.7, dec: 0.4 }, rnd);
    const k = 10 + Math.floor(rnd() * 8);
    for (let i = 0; i < k; i++) {
      noiseBand(A.ctx, A.out, t + rnd() * 1.1, A.n.white, {
        dur: R(rnd, 0.006, 0.022), f0: R(rnd, 1400, 4600), q: 4, gain: R(rnd, 0.06, 0.20) * g, atk: 0.0008, dec: 0.014,
      }, rnd);
    }
  } },

  war_drum: { bus: 'ambience', gap: 0.05, len: 1.2, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.004, type: 'lowpass', f0: 2200, gain: 0.30 * g }, rnd);
    bodyThump(A.ctx, A.out, t, { f0: J(rnd, 98, 0.06), f1: J(rnd, 58, 0.06), dur: 0.55, gain: 0.85 * g, pitchDur: 0.3 }, rnd);
    noiseBand(A.ctx, A.out, t, A.n.pink, { dur: 0.20, f0: J(rnd, 380, 0.15), f1: 180, q: 1.4, gain: 0.26 * g, atk: 0.001, dec: 0.19 }, rnd);
  } },

  war_horn: { bus: 'ambience', gap: 0.3, len: 3.0, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    const f = J(rnd, p.rate ? 116 * p.rate : 116, 0.03);
    const lp = filter(A.ctx, 'lowpass', 900, 1.6);
    const gg = gainNode(A.ctx, 0);
    envASR(gg.gain, t, 0.30 * g, 0.16, R(rnd, 0.7, 1.1), 0.9);
    sweep(lp.frequency, t, 500, 1500, 0.3);
    lp.connect(gg); gg.connect(A.out);
    for (let i = 0; i < 3; i++) {
      const o1 = A.ctx.createOscillator();
      o1.type = i === 2 ? 'triangle' : 'sawtooth';
      const fi = f * (i === 2 ? 0.5 : 1) * (1 + (i - 1) * 0.006);
      o1.frequency.setValueAtTime(fi * 0.94, t);
      o1.frequency.linearRampToValueAtTime(fi, t + 0.13);            // the scoop up to pitch
      const og = gainNode(A.ctx, i === 2 ? 0.5 : 0.4);
      o1.connect(og); og.connect(lp); o1.start(t); o1.stop(t + 2.6);
    }
  } },

  ko_chime: { bus: 'sfx', gap: 0.2, len: 2.6, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    // FM bell: a sine carrier whose frequency is modulated at an inharmonic ratio
    for (let k = 0; k < 2; k++) {
      const tt = t + k * 0.13;
      const f = (k === 0 ? 784 : 1176) * (1 + (rnd() * 2 - 1) * 0.004);
      const car = A.ctx.createOscillator(); car.type = 'sine'; car.frequency.setValueAtTime(f, tt);
      const mod = A.ctx.createOscillator(); mod.type = 'sine'; mod.frequency.setValueAtTime(f * 3.51, tt);
      const mg = gainNode(A.ctx, 0); env(mg.gain, tt, f * 2.4, 0.002, 0.55);
      mod.connect(mg); mg.connect(car.frequency);
      const cg = gainNode(A.ctx, 0); env(cg.gain, tt, (k === 0 ? 0.26 : 0.18) * g, 0.004, 1.7);
      car.connect(cg); cg.connect(A.out);
      car.start(tt); car.stop(tt + 2.0); mod.start(tt); mod.stop(tt + 2.0);
    }
    metalBank(A.ctx, A.out, t, { f0: 392, dur: 1.6, gain: 0.10 * g, n: 4, bp: 1400, bpTo: 700, q: 1.1 }, rnd);
  } },
  ko: { alias: 'ko_chime' },

  /* --- UI ---------------------------------------------------------------- */
  ui_click: { bus: 'sfx', gap: 0.01, len: 0.1, build: (A, t, p) => {
    const rnd = A.rnd, g = p.gain;
    transient(A.ctx, A.out, t, A.n.white, { dur: 0.003, f0: 5000, gain: 0.18 * g }, rnd);
    const o1 = A.ctx.createOscillator(); o1.type = 'sine'; o1.frequency.setValueAtTime(1480, t);
    const gg = gainNode(A.ctx, 0); env(gg.gain, t, 0.16 * g, 0.001, 0.045);
    o1.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(t + 0.09);
  } },
  ui_hover: { bus: 'sfx', gap: 0.01, len: 0.08, build: (A, t, p) => {
    const o1 = A.ctx.createOscillator(); o1.type = 'sine'; o1.frequency.setValueAtTime(2100, t);
    const gg = gainNode(A.ctx, 0); env(gg.gain, t, 0.07 * p.gain, 0.001, 0.03);
    o1.connect(gg); gg.connect(A.out); o1.start(t); o1.stop(t + 0.06);
  } },
  ui_confirm: { bus: 'sfx', gap: 0.02, len: 0.4, build: (A, t, p) => {
    for (let i = 0; i < 2; i++) {
      const o1 = A.ctx.createOscillator(); o1.type = 'triangle';
      o1.frequency.setValueAtTime(i ? 1320 : 880, t + i * 0.07);
      const gg = gainNode(A.ctx, 0); env(gg.gain, t + i * 0.07, 0.15 * p.gain, 0.003, 0.18);
      o1.connect(gg); gg.connect(A.out); o1.start(t + i * 0.07); o1.stop(t + i * 0.07 + 0.3);
    }
  } },
  ui_back: { bus: 'sfx', gap: 0.02, len: 0.3, build: (A, t, p) => {
    for (let i = 0; i < 2; i++) {
      const o1 = A.ctx.createOscillator(); o1.type = 'triangle';
      o1.frequency.setValueAtTime(i ? 440 : 660, t + i * 0.06);
      const gg = gainNode(A.ctx, 0); env(gg.gain, t + i * 0.06, 0.13 * p.gain, 0.003, 0.14);
      o1.connect(gg); gg.connect(A.out); o1.start(t + i * 0.06); o1.stop(t + i * 0.06 + 0.24);
    }
  } },
};

/** Resolve `alias` chains once at boot. */
export function resolve(id) {
  let d = SFX[id], n = 0;
  while (d && d.alias && n++ < 4) d = SFX[d.alias];
  return d && d.build ? d : null;
}

export const SFX_IDS = Object.keys(SFX);
